import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  InputAdornment,
  Avatar,
  Rating,
  MenuItem,
  Grid2 as Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FormatQuote as QuoteIcon,
  Search as SearchIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { cmsService, CMSTestimonial, CMSPage } from '../../services';

const CMSTestimonials: React.FC = () => {
  const { t } = useTranslation();
  const [testimonials, setTestimonials] = useState<CMSTestimonial[]>([]);
  const [pages, setPages] = useState<CMSPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState<Partial<CMSTestimonial> | null>(null);
  const [saving, setSaving] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; item: CMSTestimonial | null }>({
    open: false,
    item: null,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [testimonialsRes, pagesRes] = await Promise.all([
        cmsService.getTestimonials(),
        cmsService.getPages(),
      ]);
      setTestimonials(testimonialsRes.data.data || []);
      setPages(pagesRes.data.data || []);
    } catch (err: any) {
      setError(err.message || t('cms.errorLoadingTestimonials'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenEdit = (testimonial?: CMSTestimonial) => {
    if (testimonial) {
      setEditData(testimonial);
    } else {
      setEditData({
        citacao: '',
        autor_nome: '',
        autor_cargo: '',
        autor_empresa: '',
        autor_avatar: '',
        avaliacao: 5,
        ordem: 0,
        status: 'ativo',
        pagina_id: undefined,
      });
    }
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!editData?.citacao || !editData?.autor_nome) {
      setError(t('cms.quoteAuthorRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const testimonialData = {
        pagina_id: editData.pagina_id,
        citacao: editData.citacao,
        autor_nome: editData.autor_nome,
        autor_cargo: editData.autor_cargo || '',
        autor_empresa: editData.autor_empresa || '',
        autor_avatar: editData.autor_avatar || '',
        avaliacao: editData.avaliacao || 5,
        ordem: editData.ordem || 0,
        status: editData.status || 'ativo',
      };

      if (editData.id) {
        await cmsService.updateTestimonial(editData.id, testimonialData);
        setSuccess(t('cms.testimonialUpdated'));
      } else {
        await cmsService.createTestimonial(testimonialData);
        setSuccess(t('cms.testimonialCreated'));
      }
      setEditDialog(false);
      setEditData(null);
      fetchData();
    } catch (err: any) {
      setError(err.message || t('cms.errorSavingTestimonial'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.item) return;
    try {
      await cmsService.deleteTestimonial(deleteDialog.item.id);
      setSuccess(t('cms.testimonialDeleted'));
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err: any) {
      setError(err.message || t('cms.errorDeletingTestimonial'));
    }
  };

  const filteredTestimonials = testimonials.filter(t => {
    if (!searchTerm) return true;
    return (
      t.citacao.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.autor_nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.autor_empresa?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t('cms.testimonialsTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cms.testimonialsSubtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenEdit()}
        >
          {t('cms.newTestimonial')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('cms.searchTestimonials')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">{t('common.total')}</Typography>
              <Typography variant="h4" fontWeight="bold">{testimonials.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">{t('cms.activeCount')}</Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {testimonials.filter(t => t.status === 'ativo').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary">{t('cms.averageRating')}</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h4" fontWeight="bold">
                  {testimonials.length > 0
                    ? (testimonials.reduce((acc, t) => acc + t.avaliacao, 0) / testimonials.length).toFixed(1)
                    : '0'}
                </Typography>
                <StarIcon color="warning" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('cms.author')}</TableCell>
                <TableCell>{t('cms.quote')}</TableCell>
                <TableCell>{t('cms.page')}</TableCell>
                <TableCell>{t('cms.rating')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredTestimonials.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <QuoteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      {t('cms.noTestimonialFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTestimonials.map((testimonial) => (
                  <TableRow key={testimonial.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={testimonial.autor_avatar} sx={{ width: 40, height: 40 }}>
                          {testimonial.autor_nome.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {testimonial.autor_nome}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {testimonial.autor_cargo} {testimonial.autor_empresa && `- ${testimonial.autor_empresa}`}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        "{testimonial.citacao}"
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {testimonial.pagina_titulo ? (
                        <Chip size="small" label={testimonial.pagina_titulo} variant="outlined" />
                      ) : (
                        <Chip size="small" label={t('cms.global')} color="info" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Rating value={testimonial.avaliacao} readOnly size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={testimonial.status === 'ativo' ? 'success' : 'default'}
                        label={testimonial.status === 'ativo' ? t('common.active') : t('common.inactive')}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('common.edit')}>
                        <IconButton size="small" onClick={() => handleOpenEdit(testimonial)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, item: testimonial })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editData?.id ? t('cms.editTestimonial') : t('cms.newTestimonial')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('cms.quote')}
                value={editData?.citacao || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, citacao: e.target.value }))}
                required
                placeholder={t('cms.quotePlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('cms.authorName')}
                value={editData?.autor_nome || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, autor_nome: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('cms.position')}
                value={editData?.autor_cargo || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, autor_cargo: e.target.value }))}
                placeholder={t('cms.positionPlaceholder')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('cms.company')}
                value={editData?.autor_empresa || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, autor_empresa: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('cms.avatarUrl')}
                value={editData?.autor_avatar || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, autor_avatar: e.target.value }))}
                placeholder="https://..."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('cms.page')}
                value={editData?.pagina_id || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, pagina_id: e.target.value ? Number(e.target.value) : undefined }))}
                helperText={t('cms.emptyForAll')}
              >
                <MenuItem value="">{t('cms.globalAllPages')}</MenuItem>
                {pages.map((page) => (
                  <MenuItem key={page.id} value={page.id}>{page.titulo}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box>
                <Typography variant="body2" gutterBottom>{t('cms.rating')}</Typography>
                <Rating
                  value={editData?.avaliacao || 5}
                  onChange={(_, value) => setEditData(prev => ({ ...prev, avaliacao: value || 5 }))}
                  size="large"
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={editData?.status || 'ativo'}
                onChange={(e) => setEditData(prev => ({ ...prev, status: e.target.value as 'ativo' | 'inativo' }))}
              >
                <MenuItem value="ativo">{t('common.active')}</MenuItem>
                <MenuItem value="inativo">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={t('cms.order')}
                value={editData?.ordem || 0}
                onChange={(e) => setEditData(prev => ({ ...prev, ordem: parseInt(e.target.value) || 0 }))}
                helperText={t('cms.orderHelperText')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !editData?.citacao || !editData?.autor_nome}
          >
            {saving ? t('common.saving') : editData?.id ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, item: null })}
      >
        <DialogTitle>{t('cms.deleteTestimonial')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cms.confirmDeleteTestimonial', { name: deleteDialog.item?.autor_nome })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CMSTestimonials;
